import React, { useState, useEffect, useMemo } from 'react';
import { getUserHistory } from '../services/historyService';
import { useAuth } from '../contexts/AuthContext';
import VideoGrid from '../components/VideoGrid';

const RecentlyAnalyzedPage = () => {
  const { currentUser } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('recent'); // 'recent' or 'oldest'
  const [searchDate, setSearchDate] = useState('');

  useEffect(() => {
    if (currentUser) {
      loadVideos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadVideos = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const userHistory = await getUserHistory(currentUser.uid, 100);
      setVideos(Array.isArray(userHistory) ? userHistory : []);
    } catch (err) {
      console.error('Failed to load videos:', err);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort videos
  const filteredAndSortedVideos = useMemo(() => {
    let filtered = [...videos];

    // Filter by date if searchDate is provided
    if (searchDate) {
      const searchDateObj = new Date(searchDate);
      const searchDateStart = new Date(searchDateObj.getFullYear(), searchDateObj.getMonth(), searchDateObj.getDate());
      const searchDateEnd = new Date(searchDateStart);
      searchDateEnd.setDate(searchDateEnd.getDate() + 1);

      filtered = filtered.filter(video => {
        if (!video.lastAnalyzedAt) return false;
        const videoDate = typeof video.lastAnalyzedAt === 'number' 
          ? new Date(video.lastAnalyzedAt)
          : new Date(video.lastAnalyzedAt);
        return videoDate >= searchDateStart && videoDate < searchDateEnd;
      });
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = a.lastAnalyzedAt ? (typeof a.lastAnalyzedAt === 'number' ? a.lastAnalyzedAt : new Date(a.lastAnalyzedAt).getTime()) : 0;
      const dateB = b.lastAnalyzedAt ? (typeof b.lastAnalyzedAt === 'number' ? b.lastAnalyzedAt : new Date(b.lastAnalyzedAt).getTime()) : 0;
      
      return sortOrder === 'recent' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [videos, sortOrder, searchDate]);

  const handleToggleFavorite = async (videoId, currentFavoriteStatus, e) => {
    e.stopPropagation();
    // Favorites functionality handled on Favourites page
  };

  const handleDelete = async (videoId, e) => {
    e.stopPropagation();
    // Delete functionality - could be added if needed
  };

  const handleSelectVideo = (videoId, e) => {
    e.stopPropagation();
    // Selection functionality - could be added if needed
  };

  return (
    <div style={{
      fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
      background: 'linear-gradient(135deg, #1a1b2e 0%, #2d1b3d 100%)',
      color: '#ffffff',
      minHeight: '100vh',
      padding: '30px',
    }}>
      {/* Header with Filters */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          <label style={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.7)',
          }}>
            Sort by:
          </label>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            style={{
              padding: '10px 16px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <option value="recent">Recent First</option>
            <option value="oldest">Oldest First</option>
          </select>

          <label style={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.7)',
            marginLeft: '16px',
          }}>
            Search by date:
          </label>
          <input
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            style={{
              padding: '10px 16px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
            }}
          />
          {searchDate && (
            <button
              onClick={() => setSearchDate('')}
              style={{
                padding: '10px 16px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
        </div>

        <div style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.7)',
        }}>
          Showing {filteredAndSortedVideos.length} video{filteredAndSortedVideos.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Video Grid */}
      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid rgba(255, 255, 255, 0.2)',
            borderTopColor: '#6C5CE7',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}></div>
          <p style={{
            marginTop: '16px',
            color: 'rgba(255, 255, 255, 0.7)',
          }}>
            Loading videos...
          </p>
        </div>
      ) : filteredAndSortedVideos.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px',
          textAlign: 'center',
        }}>
          <p style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '8px',
          }}>
            {searchDate ? 'No videos found for this date.' : 'No videos analyzed yet.'}
          </p>
          <p style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.5)',
          }}>
            {searchDate ? 'Try selecting a different date.' : 'Analyze a YouTube video to see it here!'}
          </p>
        </div>
      ) : (
        <VideoGrid
          videos={filteredAndSortedVideos}
          onToggleFavorite={handleToggleFavorite}
          onDelete={handleDelete}
          onSelectVideo={handleSelectVideo}
          selectedVideos={[]}
          showCheckbox={false}
          showFavorite={false}
          showDelete={false}
          canFavorite={false}
        />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RecentlyAnalyzedPage;

